import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Check, Loader2, AlertCircle, Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";
import {
  useCreateDemoAccount,
  useDemoProvisionStatus,
  type DemoSize,
  type DemoMode,
} from "@/hooks/use-demo-account";

const SIZE_OPTIONS: { value: DemoSize; label: string; description: string }[] =
  [
    {
      value: "small",
      label: "Small",
      description: "~2,800 records · takes seconds",
    },
    {
      value: "medium",
      label: "Medium",
      description: "~8,000 records · takes a minute",
    },
    {
      value: "large",
      label: "Large",
      description: "~20,000 records · takes several minutes",
    },
  ];

const MODE_OPTIONS: { value: DemoMode; label: string; description: string }[] =
  [
    {
      value: "fast",
      label: "Fast",
      description: "Skips enrichment. Recommended.",
    },
    {
      value: "full",
      label: "Full pipeline",
      description: "Runs enrichment. Slower, uses credits.",
    },
  ];

export function AddDemoAccount() {
  const queryClient = useQueryClient();
  const { open, batchId, closeDialog, setBatchId } = useAddDemoAccountStore();
  const [size, setSize] = useState<DemoSize>("small");
  const [mode, setMode] = useState<DemoMode>("fast");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // The CREATE response carries account_id synchronously; the status poll does
  // NOT. Hold it locally so the auto-switch has a real target before the first
  // poll resolves (ADV-1286: status poll never returned account_id).
  const [accountId, setAccountId] = useState<string | undefined>(undefined);

  const createMutation = useCreateDemoAccount();
  const {
    phase,
    completedJobs,
    totalJobs,
    failedJobs,
    switchError,
    retrySwitch,
  } = useDemoProvisionStatus(batchId, accountId, size);

  const isProvisioning = phase === "provisioning" || phase === "switching";
  // Percent of import jobs the pipeline has finished. Indeterminate (null)
  // until the first poll reports a non-zero total_jobs.
  const progressPct =
    totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : null;

  function handleCreate() {
    createMutation.mutate(
      { size, mode },
      {
        onSuccess: (data) => {
          setAccountId(data.account_id);
          // batch_id is null when the import workflow is disabled — there is
          // nothing to poll, so leave batchId null and surface the (already
          // created) account via the close-time my-accounts refresh.
          setBatchId(data.batch_id);
        },
      },
    );
  }

  function handleClose() {
    // The account is created server-side as soon as provisioning starts, even
    // if the auto-switch hasn't fired. Refresh the account list on close so a
    // created-but-not-switched demo still surfaces in the switcher/settings.
    if (batchId) {
      queryClient.invalidateQueries({ queryKey: ["my-accounts"] });
    }
    closeDialog();
    setSize("small");
    setMode("fast");
    setAdvancedOpen(false);
    setAccountId(undefined);
    createMutation.reset();
  }

  function handleRetry() {
    // Drop the prior batch's cached status so its stale terminal state can't
    // flash an incorrect panel before the new attempt resolves.
    if (batchId) {
      queryClient.removeQueries({
        queryKey: ["demo-provision-status", batchId],
      });
    }
    setBatchId(null);
    setAccountId(undefined);
    createMutation.reset();
  }

  const showIntro = !batchId && !createMutation.isPending;
  const showProgress = phase === "provisioning" || phase === "switching";
  const showSuccess = phase === "completed";
  const showSwitchFailed = phase === "switchFailed";
  const showFailed = phase === "failed";
  const showTimeout = phase === "timedOut";
  const showPollError = phase === "error";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg bg-[color:var(--color-surface)] border-[color:var(--color-border)]">
        <DialogHeader>
          <DialogTitle className="typo-heading text-[color:var(--color-text-hi)]">
            Explore Adva with a demo account
          </DialogTitle>
          <DialogDescription className="typo-body text-[color:var(--color-text-md)]">
            See Adva working with a fully-populated sample home-services
            business.
          </DialogDescription>
        </DialogHeader>

        {showIntro && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Card className="border-[color:var(--color-border)] bg-[color:var(--color-elevated)]">
                <CardContent className="p-3">
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    Canopy Turf
                  </p>
                  <p className="typo-label text-[color:var(--color-text-lo)]">
                    2,030 records
                  </p>
                </CardContent>
              </Card>
              <Card className="border-[color:var(--color-border)] bg-[color:var(--color-elevated)]">
                <CardContent className="p-3">
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    Canopy Coatings
                  </p>
                  <p className="typo-label text-[color:var(--color-text-lo)]">
                    752 records
                  </p>
                </CardContent>
              </Card>
            </div>

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-1 typo-label text-[color:var(--color-text-lo)] hover:text-[color:var(--color-text-hi)] transition-colors">
                  <ChevronRight
                    className={cn(
                      "size-3 transition-transform",
                      advancedOpen && "rotate-90",
                    )}
                  />
                  Advanced
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-4">
                <div>
                  <p className="typo-label text-[color:var(--color-text-lo)] mb-2">
                    Data volume
                  </p>
                  <RadioGroup
                    value={size}
                    onValueChange={(v) => setSize(v as DemoSize)}
                    className="grid grid-cols-3 gap-2"
                  >
                    {SIZE_OPTIONS.map((opt) => (
                      <Label
                        key={opt.value}
                        htmlFor={`size-${opt.value}`}
                        className={cn(
                          "flex cursor-pointer flex-col gap-1 rounded-lg border-2 p-2.5 transition-colors",
                          size === opt.value
                            ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5"
                            : "border-[color:var(--color-border)] hover:border-[color:var(--color-border)]/80",
                        )}
                      >
                        <div className="flex items-start gap-1.5">
                          <RadioGroupItem
                            value={opt.value}
                            id={`size-${opt.value}`}
                            className="mt-0.5 shrink-0"
                          />
                          <div>
                            <span className="block typo-body font-medium text-[color:var(--color-text-hi)]">
                              {opt.label}
                            </span>
                            <span className="block typo-label text-[color:var(--color-text-lo)]">
                              {opt.description}
                            </span>
                          </div>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <p className="typo-label text-[color:var(--color-text-lo)] mb-2">
                    Import mode
                  </p>
                  <RadioGroup
                    value={mode}
                    onValueChange={(v) => setMode(v as DemoMode)}
                    className="grid grid-cols-2 gap-2"
                  >
                    {MODE_OPTIONS.map((opt) => (
                      <Label
                        key={opt.value}
                        htmlFor={`mode-${opt.value}`}
                        className={cn(
                          "flex cursor-pointer flex-col gap-1 rounded-lg border-2 p-2.5 transition-colors",
                          mode === opt.value
                            ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5"
                            : "border-[color:var(--color-border)] hover:border-[color:var(--color-border)]/80",
                        )}
                      >
                        <div className="flex items-start gap-1.5">
                          <RadioGroupItem
                            value={opt.value}
                            id={`mode-${opt.value}`}
                            className="mt-0.5 shrink-0"
                          />
                          <div>
                            <span className="block typo-body font-medium text-[color:var(--color-text-hi)]">
                              {opt.label}
                            </span>
                            <span className="block typo-label text-[color:var(--color-text-lo)]">
                              {opt.description}
                            </span>
                          </div>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {createMutation.isError && (
              <div className="flex items-start gap-2 rounded-lg border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5 p-3">
                <AlertCircle className="size-4 mt-0.5 shrink-0 text-[color:var(--color-danger)]" />
                <p className="typo-label text-[color:var(--color-text-md)]">
                  Couldn't start your demo account.{" "}
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : "Please try again."}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Creating…
                  </>
                ) : createMutation.isError ? (
                  "Try again"
                ) : (
                  "Create demo account"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {showProgress && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 typo-body">
              <Loader2 className="size-3.5 animate-spin text-[color:var(--color-accent)]" />
              <span className="flex-1 text-[color:var(--color-text-md)]">
                Setting up your demo account…
              </span>
              {progressPct !== null && (
                <span className="typo-mono text-[color:var(--color-text-lo)]">
                  {completedJobs.toLocaleString()}/{totalJobs.toLocaleString()}{" "}
                  steps
                </span>
              )}
            </div>
            {/* Non-interactive determinate track — fills as the import pipeline
                completes jobs. Indeterminate (full muted track) until the first
                poll reports a total. */}
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-border)]"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPct ?? undefined}
              aria-label="Demo account setup progress"
            >
              <div
                className={cn(
                  "h-full bg-[color:var(--color-accent)] transition-[width] duration-500",
                  progressPct === null && "animate-pulse",
                )}
                style={{ width: `${progressPct ?? 100}%` }}
              />
            </div>
            {isProvisioning && (
              <p className="typo-label text-[color:var(--color-text-lo)]">
                You'll be switched into the demo account when it's ready.
              </p>
            )}
          </div>
        )}

        {showSuccess && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Check className="size-4 text-[color:var(--color-success)]" />
              <p className="typo-body text-[color:var(--color-text-hi)]">
                Demo account ready. You've been switched in.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Go to demo</Button>
            </DialogFooter>
          </div>
        )}

        {showSwitchFailed && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="size-4 text-[color:var(--color-warning)]" />
              <p className="typo-body text-[color:var(--color-text-hi)]">
                Demo account created — but we couldn't switch you into it.
              </p>
            </div>
            <p className="typo-label text-[color:var(--color-text-md)]">
              {switchError instanceof Error
                ? switchError.message
                : "Your demo account is ready. Switch to it now, or from Settings later."}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={retrySwitch}>Switch to demo</Button>
            </DialogFooter>
          </div>
        )}

        {showTimeout && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-[color:var(--color-warning)]" />
              <p className="typo-body text-[color:var(--color-text-hi)]">
                This is taking longer than expected.
              </p>
            </div>
            <p className="typo-label text-[color:var(--color-text-md)]">
              Your demo account may still be setting up in the background. Check
              the account switcher in a few minutes, or start over.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={handleRetry}>Start over</Button>
            </DialogFooter>
          </div>
        )}

        {showPollError && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="size-4 text-[color:var(--color-danger)]" />
              <p className="typo-body text-[color:var(--color-text-hi)]">
                We lost track of your demo account setup.
              </p>
            </div>
            <p className="typo-label text-[color:var(--color-text-md)]">
              The account may still have been created — check the account
              switcher, or try again.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={handleRetry}>Try again</Button>
            </DialogFooter>
          </div>
        )}

        {showFailed && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="size-4 text-[color:var(--color-danger)]" />
              <p className="typo-body text-[color:var(--color-text-hi)]">
                Demo account setup failed.
              </p>
            </div>
            {totalJobs > 0 && (
              <p className="typo-label text-[color:var(--color-text-md)]">
                {failedJobs.toLocaleString()} of {totalJobs.toLocaleString()}{" "}
                import {failedJobs === 1 ? "step" : "steps"} didn't finish.
                {completedJobs > 0 &&
                  ` ${completedJobs.toLocaleString()} completed before the failure.`}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleRetry}>Try again</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
